import { useEffect, useState } from "react";
import axios from "axios";
import NavBar from "../../components/NavBar/NavBar";
import MeasurementFilters from "../../components/Forms/MeasurementFilters";
import PaginationControls from "../../components/Pagination/PaginationControls";
import MeasurementRunTable from "../../components/Tables/MeasurementRunTable";
import MeasurementRunForm from "../../components/Forms/MeasurementRunForm";

const API_BASE_URL =  process.env.REACT_APP_API_BASE_URL;

const MeasurementRunPage = ({ categories, auth, onLogout }) => {
    const [measurements, setMeasurements] = useState([]);
    const [sensors, setSensors] = useState([]);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMeasurement, setSelectedMeasurement] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [fileContent, setFileContent] = useState('');

    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        order: 'desc',
        stations: [],
        measurement_type: '',
        device_id: '',
    });

    // console.log("filters", filters)
    const [totalPages, setTotalPages] = useState(1);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null)

            const headers = { Authorization: `Bearer ${auth.token}` }; // authorization header

            // Convert filters to API parameters
            const params = {
                ...filters,
                device_id: filters.device_id ? Number(filters.device_id) : undefined,
                stations: Array.isArray(filters.stations) ? filters.stations.join(',') : filters.stations,
                measurement_type: filters.measurement_type || undefined,
            };

            const [measurementsResponse, devicesResponse, sensorsResponse] = await Promise.all([
                axios.get(`${API_BASE_URL}/measurements`, { params, headers }),
                axios.get(`${API_BASE_URL}/devices`, { headers }),
                axios.get(`${API_BASE_URL}/sensors`, { headers }),
            ]);

            // console.log("measurements response.data", measurementsResponse.data)

            setMeasurements(measurementsResponse.data.measurements);
            setTotalPages(measurementsResponse.data.meta.numOfPages);
            setDevices(devicesResponse.data);
            setSensors(sensorsResponse.data);

            // console.log("Types fetched:", typesResponse.data);
        } catch (error) {
            if (error.response?.status === 401) {
                onLogout(); // Trigger logout on 401 error
            } else {
                setError(error.response?.data?.message || "Failed to load data. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // Add pagination handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setFilters(prev => ({ ...prev, page: newPage }));
        }
    };

    const handleLimitChange = (newLimit) => {
        setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
    };

    useEffect(() => {
        if (auth.token) {
            fetchData();
        } else {
            setError("Authentication token missing");
            setLoading(false);
        }
    }, [filters, auth.token]);

    const handleSaveMeasurement = async (measurement) => {
        try {
            const headers = { Authorization: `Bearer ${auth.token}` }; // authorization header

            if (measurement.id) {
                const response = await axios.put(`${API_BASE_URL}/measurements/${measurement.id}`, measurement, { headers });

                // Update the measurement
                setMeasurements((prevMeas) =>
                    prevMeas.map((m) => m.id === measurement.id ? response.data.measurement[0] : m)
                );
            } else {
                let response = await axios.post(`${API_BASE_URL}/measurements`, measurement, { headers });
                setMeasurements(prev => [response.data.measurement[0], ...prev]);
            }
            setShowModal(false);
            setSelectedMeasurement(null);
            await fetchData();
        } catch (error) {
            if (error.response?.status === 401) {
                onLogout(); // Trigger logout on 401 error
            } else {
                setError(error.response?.data?.message || "Failed to save measurement.");
                throw error;
            }
        }
    };

    const handleDeleteMeasurement = async (id) => {
        if (!window.confirm("Are you sure you want to delete this measurement?")) return;

        try {
            await axios.delete(`${API_BASE_URL}/measurements/${id}`, {
                headers: { Authorization: `Bearer ${auth.token}` } // Add Authorization header
            });
            setMeasurements(prev => prev.filter(m => m.id !== id));
            await fetchData();
        } catch (error) {
            if (error.response?.status === 401) {
                onLogout(); // Trigger logout on 401 error
            } else {
                setError(error.response?.data?.message || "Failed to delete measurement. Please try again.");
            }
        }
    };

    const handleViewMeasurement = async (measurement) => {
        setSelectedMeasurement(measurement);

        try {
            // console.log("single measurement", measurement)
            const filePath = measurement.user + '/' + measurement.filename; // File path

            const response = await axios.get(`${API_BASE_URL}/files/file`, {
                params: { path: filePath },
                headers: { Authorization: `Bearer ${auth.token}` } // Add Authorization header
            });

            setFileContent(response.data.content); // Save file content
        } catch (error) {
            if (error.response?.status === 401) {
                onLogout(); // Trigger logout on 401 error
            } else {
                setError(error.response?.data?.message || "Unable to retrieve file content.");
            }
        }
    };

    return (
        <>
            <NavBar auth={auth} onLogout={onLogout} />
            {error && <div className="alert alert-danger" style={{ margin: "1rem" }}>{error}</div>}

            <MeasurementFilters
                devices={devices}
                measurementTypes={categories}
                filters={filters}
                setFilters={setFilters}
                onSearch={fetchData}
                onReset={() => {
                    setFilters({
                        page: 1,
                        limit: 10,
                        order: 'desc',
                        stations: [], // reset stations to an empty array
                        status: '',
                        measurement_type: '',
                        status:'',
                        device_id: '',
                    });
                }}
            />

            <MeasurementRunTable
                measurements={measurements}
                loading={loading}
                onEdit={measurement => {
                    setSelectedMeasurement(measurement);
                    setShowModal(true);
                }}
                onDelete={handleDeleteMeasurement}
                onView={handleViewMeasurement}
                fileContent={fileContent}
                auth={auth}
                onAddNewMeasurement={() => {
                    setSelectedMeasurement(null);
                    setShowModal(true);
                }}
            />

            <PaginationControls
                currentPage={filters.page}
                totalPages={totalPages}
                limit={filters.limit}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
            />

            <MeasurementRunForm
                sensors={sensors}
                devices={devices}
                measurementTypes={categories}
                selectedMeasurement={selectedMeasurement}
                showModal={showModal}
                setShowModal={setShowModal}
                onSave={handleSaveMeasurement}
                auth={auth}
                onLogout={onLogout}
            />
        </>
    );
};

export default MeasurementRunPage;